<template>
  <div class="agent-text-reply">
    <div class="agent-text-reply-head">
      <div>
        <strong>Text Reply</strong>
        <small>Opsional. Pilih teks balasan yang bisa digunakan Chatbot.</small>
      </div>
      <span class="agent-text-reply-count">{{ rows.length }}/{{ max }}</span>
    </div>

    <div v-if="loading" class="agent-text-reply-state">
      <i class="fa fa-spinner fa-spin"></i> Memuat pilihan Text Reply...
    </div>

    <div v-else-if="error" class="agent-text-reply-state is-error">
      <span>{{ error }}</span>
      <button type="button" class="btn btn-link btn-sm" @click="$emit('reload')">Coba lagi</button>
    </div>

    <template v-else>
      <div v-if="!optionList.length" class="agent-text-reply-empty">
        Belum ada Text Reply yang tersedia. Buat Text Reply terlebih dahulu dari menu Text Reply.
      </div>

      <div v-for="(row, index) in rows" :key="index" class="agent-text-reply-row">
        <label :for="fieldId(index)">Text Reply {{ index + 1 }}</label>
        <select
          :id="fieldId(index)"
          class="form-control"
          :value="row.auto_reply_button_id"
          @change="onSelect(index, $event)"
        >
          <option value="">Pilih teks balasan</option>
          <option
            v-for="option in optionList"
            :key="option.id"
            :value="option.id"
            :disabled="isOptionDisabled(option.id, index)"
          >
            {{ option.title }}
          </option>
          <option v-if="rows.length < max" :value="createValue">
            + Tambah Text Reply Baru
          </option>
        </select>
        <button
          type="button"
          class="agent-text-reply-remove"
          aria-label="Hapus pilihan Text Reply"
          @click="remove(index)"
        >
          <i class="fa fa-trash-o"></i>
        </button>
      </div>

      <div class="agent-text-reply-foot">
        <agent-button
          variant="secondary"
          size="sm"
          icon="fa fa-plus"
          :disabled="!canAdd"
          @click="add"
        >
          Tambah
        </agent-button>
        <small v-if="rows.length >= max">Maksimal {{ max }} pilihan Text Reply tercapai.</small>
        <small v-else-if="!optionList.length">Belum ada Text Reply. Tambah baris lalu pilih Tambah Text Reply Baru.</small>
      </div>
    </template>
  </div>
</template>

<script>
import AgentButton from "./AgentButton.vue";
import { MAX_TEXT_REPLY } from "../utils";

export default {
  name: "TextReplyPicker",
  components: {
    AgentButton
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ""
    },
    max: {
      type: Number,
      default: MAX_TEXT_REPLY
    }
  },
  data() {
    return {
      rows: [{ auto_reply_button_id: "" }],
      createValue: "__create_text_reply__"
    };
  },
  computed: {
    optionList() {
      const list = (this.options || []).map(option => ({
        id: String(option.id || ""),
        title: option.title || option.button_title || option.button_text || "Tanpa judul"
      })).filter(option => option.id);

      const known = list.map(option => option.id);
      const missing = this.selectedIds().filter(id => known.indexOf(id) === -1);
      return list.concat(
        missing.map(id => ({
          id,
          title: "Text Reply tersimpan"
        }))
      );
    },
    canAdd() {
      return this.rows.length < this.max;
    }
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler() {
        this.syncFromValue();
      }
    }
  },
  methods: {
    fieldId(index) {
      return "agent-text-reply-" + this._uid + "-" + index;
    },
    selectedIds() {
      return (this.rows || [])
        .map(row => String((row && row.auto_reply_button_id) || ""))
        .filter(Boolean);
    },
    syncFromValue() {
      const incoming = (Array.isArray(this.value) ? this.value : [])
        .map(row => String((row && row.auto_reply_button_id) || ""))
        .filter(Boolean)
        .filter((id, index, list) => list.indexOf(id) === index)
        .slice(0, this.max);
      const current = this.selectedIds();
      if (
        incoming.length === current.length &&
        incoming.every((id, index) => id === current[index])
      ) {
        return;
      }
      this.rows = incoming.length
        ? incoming.map(id => ({ auto_reply_button_id: id }))
        : [{ auto_reply_button_id: "" }];
    },
    isOptionDisabled(id, index) {
      const value = String(id || "");
      return this.rows.some(
        (row, rowIndex) =>
          rowIndex !== index && String(row.auto_reply_button_id || "") === value
      );
    },
    onSelect(index, event) {
      const value = event && event.target ? event.target.value : "";
      if (value === this.createValue) {
        if (event && event.target) {
          event.target.value = (this.rows[index] && this.rows[index].auto_reply_button_id) || "";
        }
        this.$emit("create", index);
        return;
      }
      this.change(index, value);
    },
    selectOption(index, option) {
      if (!option || !option.id) return;
      const id = String(option.id);
      if (!this.rows[index]) {
        this.rows.splice(index, 0, { auto_reply_button_id: "" });
      }
      this.rows.forEach((row, rowIndex) => {
        if (
          rowIndex !== index &&
          String(row.auto_reply_button_id || "") === id
        ) {
          row.auto_reply_button_id = "";
        }
      });
      this.rows[index].auto_reply_button_id = id;
      this.emitRows();
    },
    change(index, value) {
      if (!this.rows[index]) return;
      this.rows[index].auto_reply_button_id = value;
      this.emitRows();
    },
    add() {
      if (!this.canAdd) return;
      this.rows.push({ auto_reply_button_id: "" });
    },
    remove(index) {
      if (index < 0 || index >= this.rows.length) return;
      this.rows.splice(index, 1);
      this.emitRows();
    },
    emitRows() {
      const seen = {};
      const payload = [];
      this.rows.forEach(row => {
        const id = String(row.auto_reply_button_id || "");
        if (!id || seen[id]) return;
        seen[id] = true;
        payload.push({
          auto_reply_button_id: id,
          button_type: "text_reply"
        });
      });
      this.$emit("input", payload);
    }
  }
};
</script>

<style scoped>
.agent-text-reply {
  margin: 14px 0;
  border: 1px solid #e9eef5;
  border-radius: 12px;
  background: #fbfcfe;
  padding: 13px;
}
.agent-text-reply-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.agent-text-reply-head strong {
  display: block;
  color: #344054;
  font-size: 13px;
  font-weight: 800;
}
.agent-text-reply-head small {
  display: block;
  margin-top: 2px;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.45;
}
.agent-text-reply-count {
  flex: 0 0 auto;
  border-radius: 999px;
  background: #eef4fb;
  color: #56708f;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 800;
}
.agent-text-reply-row {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr) 30px;
  align-items: center;
  gap: 8px;
  margin-top: 9px;
}
.agent-text-reply-row label {
  margin: 0;
  color: #475467;
  font-size: 12px;
  font-weight: 700;
}
.agent-text-reply-row .form-control {
  height: 34px;
  font-size: 12px;
}
.agent-text-reply-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #f5c2c0;
  border-radius: 7px;
  background: #fff;
  color: #d92d20;
  cursor: pointer;
}
.agent-text-reply-remove:hover,
.agent-text-reply-remove:focus {
  border-color: #d92d20;
  background: #fff6f5;
  outline: 0;
}
.agent-text-reply-foot {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 10px;
}
.agent-text-reply-foot small {
  color: #98a2b3;
  font-size: 12px;
}
.agent-text-reply-state,
.agent-text-reply-empty {
  margin-top: 10px;
  color: #7b8ba3;
  font-size: 12px;
  line-height: 1.5;
}
.agent-text-reply-state.is-error {
  color: #d92d20;
}
@media (max-width: 575px) {
  .agent-text-reply-row {
    grid-template-columns: minmax(0, 1fr) 30px;
  }
  .agent-text-reply-row label {
    grid-column: 1 / -1;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ai/agentV2/components/TextReplyPicker.vue